import React from 'react';
import { assign } from 'xstate';
import { cloneDeep } from 'lodash';
import { IntlShape } from 'react-intl';

import { MachineContext } from '../../MachineContext';
import { BaseComponent } from '../Component';
import { translateWithFallback } from '../../util';
import { ComponentSize } from '../ComponentSizeEnum';

export interface ImageDisplayComponent extends BaseComponent {
  type: 'imageDisplay';
  props: ImageDisplayProps;
}

// note on assetid
// we do not need assetid for rendering but:
// in a first rendering i a session, we could resolve the assetid
// transparent to the component. But then the resolved S3 url
// would be stored in the src tag.
// If we would load the session the next day, the S3 Url wouldn't be
// valid any more as it is only valid for a limited time.
// Thus, we have to recognize the assetid and re-load a pre-signed
// URL for displaying the session again.
// resolved src contains the s3 URL for the currently displayed session
// by that, we preserve the actual src and assetid !!
export interface ImageDisplayProps {
  src: string;
  width?: number;
  height?: number;
  alt?: string;
  assetid: string;
  resolvedsrc: string;
  componentSize?: ComponentSize;
}

export const ImageDisplay: React.FC<ImageDisplayProps> = ({
  src,
  width,
  height,
  alt,
  assetid,
  resolvedsrc,
  componentSize,
}) => {
  const procentualSize =
    componentSize === ComponentSize.SMALL
      ? '30%'
      : componentSize === ComponentSize.MEDIUM
      ? '60%'
      : '90%';
  const componentWidth = componentSize
    ? procentualSize
    : width
    ? width
    : height
    ? undefined
    : procentualSize;

  return (
    <img
      height={height}
      width={componentWidth}
      src={resolvedsrc}
      alt={alt ?? src}
      id={assetid ?? src}
    />
  );
};

export const renderImageDisplayAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;

    newContext.widgetData.push({
      type: 'imageDisplay',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        src: payload.src,
        resolvedsrc: payload.resolvedsrc,
        width: payload.width,
        height: payload.height,
        componentSize: payload.componentSize,
        alt: translateWithFallback(intl, payload.altText),
        assetid: payload.assetid,
      },
    });
    return newContext;
  });
};
